import type { AdminProfessionalServiceRow } from '../../../services/api';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { AdminDashboardEmptyState } from '../AdminDashboardPrimitives';
import { formatLinkedArtifactsTypeLabel } from './adminCreditAdapters';
import { CreditCopyButton } from './CreditCopyButton';

function formatLinkedArtifactLabel(
    linkage: AdminProfessionalServiceRow['linkages'][number],
): string | null {
    const typeLabel = formatLinkedArtifactsTypeLabel(linkage.linkedArtifactType);
    if (!typeLabel || !linkage.linkedArtifactId) {
        return null;
    }
    return `${typeLabel}: ${linkage.linkedArtifactId}`;
}

export function ProfessionalServicesTableSection({
    rows,
    onRowClick,
    onDelete,
    deletingId,
}: {
    rows: AdminProfessionalServiceRow[];
    onRowClick: (professionalServiceId: string) => void;
    onDelete: (professionalServiceId: string) => void;
    deletingId: string | null;
}) {
    if (rows.length === 0) {
        return <AdminDashboardEmptyState message="No professional services found." />;
    }

    return (
        <Card className="overflow-hidden">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[1%] whitespace-nowrap">ID</TableHead>
                        <TableHead>Professional service</TableHead>
                        <TableHead>Team</TableHead>
                        <TableHead>Linked artifacts</TableHead>
                        <TableHead className="text-right">Updated</TableHead>
                        <TableHead className="w-[8rem] text-right">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow
                            key={row.id}
                            className="cursor-pointer"
                            onClick={() => onRowClick(row.id)}
                        >
                            <TableCell className="align-top">
                                <div className="flex items-center gap-1.5">
                                    <span
                                        className="max-w-[9rem] truncate font-mono text-xs font-semibold text-neutral-900"
                                        title={row.id}
                                    >
                                        {row.id}
                                    </span>
                                    <span onClick={(event) => event.stopPropagation()}>
                                        <CreditCopyButton
                                            value={row.id}
                                            label="Copy professional service ID"
                                        />
                                    </span>
                                </div>
                            </TableCell>
                            <TableCell>
                                <p className="font-medium text-neutral-950">{row.name}</p>
                                {row.description ? (
                                    <p className="mt-1 text-xs text-neutral-500">
                                        {row.description}
                                    </p>
                                ) : null}
                            </TableCell>
                            <TableCell>{row.teamName ?? '—'}</TableCell>
                            <TableCell>
                                <div className="space-y-1">
                                    {row.linkages.map((linkage) => (
                                        <p key={linkage.id} className="text-sm text-neutral-700">
                                            {formatLinkedArtifactLabel(linkage) ?? '—'}
                                        </p>
                                    ))}
                                </div>
                            </TableCell>
                            <TableCell className="text-right text-sm text-neutral-600">
                                {new Date(row.updatedAt).toLocaleString()}
                            </TableCell>
                            <TableCell
                                className="text-right"
                                onClick={(event) => event.stopPropagation()}
                            >
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled={deletingId === row.id}
                                    onClick={() => onDelete(row.id)}
                                    aria-label={`Delete ${row.name}`}
                                >
                                    Delete
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Card>
    );
}
